/**
 * The phone camera bubble's place (spec 13.10): a centre normalized 0 to 1
 * against one panel's video as displayed, and the panel it is on. Clamped
 * against that video's current size whenever it is drawn, so no resize,
 * rotation, metadata change or recovery can put it outside the picture.
 * Pure, for unit tests.
 */
export interface BubblePos { panel: 1 | 2; x: number; y: number }

/** Bottom right of panel 1; the clamp pulls it to the insets. */
export const BUBBLE_HOME: BubblePos = { panel: 1, x: 1, y: 1 };

export interface BubbleInsets { top: number; right: number; bottom: number; left: number }

/**
 * The centre that keeps a circle of diameter `d` inside a `w` x `h` video,
 * at least the insets from each edge. A video too small for the circle and
 * its insets gets it centred on that axis.
 */
export function clampBubble(x: number, y: number, w: number, h: number, d: number, inset: BubbleInsets): { x: number; y: number } {
  if (!(w > 0) || !(h > 0)) return { x: 0.5, y: 0.5 };
  const r = d / 2;
  const axis = (v: number, size: number, lo: number, hi: number) => {
    const min = (lo + r) / size;
    const max = 1 - (hi + r) / size;
    if (min > max) return 0.5;
    return Math.min(max, Math.max(min, Number.isFinite(v) ? v : 1));
  };
  return { x: axis(x, w, inset.left, inset.right), y: axis(y, h, inset.top, inset.bottom) };
}

/** "panel:x:y". Older values held only a corner ("2:tl", "br"), read as that corner. */
export function parseBubble(raw: string | null): BubblePos {
  if (!raw) return BUBBLE_HOME;
  const pos = /^([12]):(-?\d*\.?\d+):(-?\d*\.?\d+)$/.exec(raw);
  if (pos) return { panel: pos[1] === '2' ? 2 : 1, x: Number(pos[2]), y: Number(pos[3]) };
  const corner = /^(?:([12]):)?(t|b)(l|r)$/.exec(raw);
  if (corner) return { panel: corner[1] === '2' ? 2 : 1, x: corner[3] === 'r' ? 1 : 0, y: corner[2] === 'b' ? 1 : 0 };
  return BUBBLE_HOME;
}

export function formatBubble(p: BubblePos): string {
  return `${p.panel}:${p.x.toFixed(4)}:${p.y.toFixed(4)}`;
}
